import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { useTranslation } from 'react-i18next';
import { androidColors } from '../../theme/androidTheme';
import { OverviewData, cryptoService } from '../../services/cryptoService';
import { usePriceConversion, convertUSDtoEUR } from '../../hooks/usePriceConversion';

interface OverviewChartProps {
  overviewData: OverviewData;
  title?: string;
  type?: 'line' | 'area';
}

interface CandleData {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface ApiResponse {
  symbol: string;
  period: string;
  candles: CandleData[];
  source: string;
  timestamp: string;
}

interface PortfolioAsset {
  symbol: string;
  quantity: number;
}

interface FormattedChartData {
  date: string;
  value: number;
  gain: number;
  timestamp: number;
}

const OverviewChart: React.FC<OverviewChartProps> = ({ 
  overviewData,
  title,
  type = 'area'
}) => {
  const { t } = useTranslation();
  const chartTitle = title || t('portfolio.evolution');
  const [chartData, setChartData] = useState<FormattedChartData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('1M');
  const [portfolioAssets, setPortfolioAssets] = useState<PortfolioAsset[]>([]);
  const { rate, loading: rateLoading, error: rateError } = usePriceConversion();

  useEffect(() => {
    loadPortfolioData();
  }, []);

  useEffect(() => {
    if (portfolioAssets.length > 0) {
      fetchCandlesData();
    }
  }, [selectedPeriod, rate, portfolioAssets]);

  const loadPortfolioData = async () => {
    try {
      const portfolioData = await cryptoService.getPortfolioData();
      const assets: PortfolioAsset[] = portfolioData.cryptos.map(crypto => ({
        symbol: crypto.symbol,
        quantity: crypto.amount
      }));
      setPortfolioAssets(assets);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.fetchError'));
    }
  };

  const fetchCandlesData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Récupérer les données pour chaque actif
      const candlesPromises = portfolioAssets.map(asset => 
        fetch(`https://candles-cryptonvx.candles-api.workers.dev/v1/candles?symbol=${asset.symbol}&period=${selectedPeriod}`)
          .then(response => {
            if (!response.ok) throw new Error(`Erreur lors de la récupération des données pour ${asset.symbol}`);
            return response.json();
          })
      );

      const candlesResponses = await Promise.all(candlesPromises);
      
      // Créer un map des timestamps pour aligner les données
      const timestampMap = new Map<number, { totalValue: number }>();
      
      // Pour chaque actif, calculer sa contribution à chaque timestamp
      candlesResponses.forEach((response: ApiResponse, index) => {
        const asset = portfolioAssets[index];
        response.candles.forEach(candle => {
          const timestamp = candle.timestamp;
          const value = candle.close * asset.quantity; // Valeur = prix * quantité
          
          if (timestampMap.has(timestamp)) {
            const current = timestampMap.get(timestamp)!;
            timestampMap.set(timestamp, {
              totalValue: current.totalValue + value
            });
          } else {
            timestampMap.set(timestamp, {
              totalValue: value
            });
          }
        });
      });

      // Convertir le map en tableau et calculer les valeurs
      const formattedData: FormattedChartData[] = Array.from(timestampMap.entries())
        .map(([timestamp, { totalValue }]) => ({
          date: new Date(timestamp * 1000).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
          value: convertUSDtoEUR(totalValue, rate),
          timestamp: timestamp * 1000,
          gain: 0
        }))
        .sort((a, b) => a.timestamp - b.timestamp);

      // Calculer les gains
      if (formattedData.length > 0) {
        const initialValue = formattedData[0].value;
        formattedData.forEach(data => {
          data.gain = data.value - initialValue;
        });
      }

      setChartData(formattedData);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.unknownError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (_: React.MouseEvent<HTMLElement>, newPeriod: string | null) => {
    if (newPeriod) {
      setSelectedPeriod(newPeriod);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          sx={{
            backgroundColor: androidColors.cardBackground,
            border: `1px solid ${androidColors.borderColor}`,
            borderRadius: '8px',
            p: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}
        >
          <Typography variant="subtitle2" sx={{ color: androidColors.textColor, fontWeight: 'bold' }}>
            {label}
          </Typography>
          <Typography variant="body2" sx={{ color: androidColors.textSecondary }}>
            Valeur totale: {formatCurrency(data.value)}
          </Typography>
          <Typography 
            sx={{ 
              color: data.gain >= 0 ? androidColors.success : androidColors.error 
            }}
          >
            {data.gain >= 0 ? '+' : ''}{formatCurrency(data.gain)}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const chartColor = overviewData.gain >= 0 ? androidColors.success : androidColors.error;
  const gradientId = `gradient-${type}-${overviewData.gain >= 0 ? 'positive' : 'negative'}`;

  if (rateError) {
    return (
      <Card sx={{ backgroundColor: androidColors.cardBackground, border: `1px solid ${androidColors.borderColor}`, borderRadius: '12px' }}>
        <CardContent>
          <Typography sx={{ color: androidColors.error }}>Erreur de conversion: {rateError}</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        backgroundColor: androidColors.cardBackground,
        border: `1px solid ${androidColors.borderColor}`,
        borderRadius: '12px',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography
            variant="h6"
            sx={{
              color: androidColors.textColor,
              fontWeight: 'bold',
            }}
          >
            {chartTitle}
          </Typography>
          <Box sx={{ textAlign: 'right' }}>
            <Typography
              variant="h6"
              sx={{
                color: androidColors.textColor,
                fontWeight: 'bold',
              }}
            >
              {formatCurrency(overviewData.totalCapital)}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: overviewData.gain >= 0 ? androidColors.success : androidColors.error,
                fontWeight: 'bold',
              }}
            >
              {overviewData.gain >= 0 ? '+' : ''}{formatCurrency(overviewData.gain)} ({overviewData.gainPercentage >= 0 ? '+' : ''}{overviewData.gainPercentage.toFixed(2)}%)
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <ToggleButtonGroup
            value={selectedPeriod}
            exclusive
            onChange={handlePeriodChange}
            aria-label="période"
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                color: androidColors.textSecondary,
                borderColor: androidColors.borderColor,
                '&.Mui-selected': {
                  backgroundColor: androidColors.primaryGold,
                  color: androidColors.darkBackground,
                  '&:hover': {
                    backgroundColor: androidColors.primaryGold,
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              },
            }}
          >
            <ToggleButton value="1D" aria-label="1 jour">1J</ToggleButton>
            <ToggleButton value="7D" aria-label="7 jours">7J</ToggleButton>
            <ToggleButton value="1M" aria-label="1 mois">1M</ToggleButton>
            <ToggleButton value="1Y" aria-label="1 an">1A</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        
        <Box sx={{ height: 300 }}>
          {loading || rateLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography sx={{ color: androidColors.textSecondary }}>Chargement...</Typography>
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography sx={{ color: androidColors.error }}>{error}</Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              {type === 'area' ? (
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={chartColor} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={chartColor} stopOpacity={0.05}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke={androidColors.borderColor}
                    opacity={0.3}
                  />
                  <XAxis 
                    dataKey="date" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: androidColors.textSecondary, fontSize: 12 }}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: androidColors.textSecondary, fontSize: 12 }}
                    tickFormatter={formatCurrency}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke={chartColor}
                    strokeWidth={2}
                    fill={`url(#${gradientId})`}
                    dot={false}
                    activeDot={{ 
                      r: 4, 
                      fill: chartColor,
                      stroke: androidColors.cardBackground,
                      strokeWidth: 2
                    }}
                  />
                </AreaChart>
              ) : (
                <LineChart data={chartData}>
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke={androidColors.borderColor}
                    opacity={0.3}
                  />
                  <XAxis 
                    dataKey="date" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: androidColors.textSecondary, fontSize: 12 }}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: androidColors.textSecondary, fontSize: 12 }}
                    tickFormatter={formatCurrency}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={chartColor}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ 
                      r: 4, 
                      fill: chartColor,
                      stroke: androidColors.cardBackground,
                      strokeWidth: 2
                    }}
                  />
                </LineChart>
              )}
            </ResponsiveContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default OverviewChart;